import type { SiteText } from "./types.js";

export const en: SiteText = {
  nav: { pricing: "Pricing", terms: "Terms", refund: "Refunds", privacy: "Privacy", login: "Log in", home: "Home" },
  hero: {
    title: "Booking software with AI replies",
    text: "A calendar for salons and barbershops that work by appointment. The AI assistant answers clients' questions in Instagram messages and books appointments into your calendar, using only the information the salon has entered.",
    cta: "See pricing",
    login: "Salon login",
    note: "No prepayments, no bulk messages. Staff always stay in charge.",
  },
  demo: {
    chatTitle: "Instagram Direct",
    messages: [
      { from: "client", text: "Hi! Any slot tomorrow after 17:00 for a fade?" },
      { from: "assistant", text: "Hi! I have 17:30 with Marko or 18:00 with Nikola. A fade takes 30 min, 1,500 RSD. Which one works for you?" },
      { from: "client", text: "17:30 with Marko." },
      { from: "assistant", text: "Fade with Marko tomorrow at 17:30, 1,500 RSD. Shall I book it?" },
      { from: "client", text: "Yes." },
      { from: "assistant", text: "Booked: tomorrow at 17:30, fade with Marko. See you!" },
    ],
    bookingTitle: "Calendar",
    booking: { time: "17:30–18:00", client: "Jelena", details: "Fade · Marko · booked by the assistant", status: "confirmed" },
  },
  how: {
    title: "How it works",
    steps: [
      { title: "Connect Instagram", text: "The salon's professional account is connected through the Meta dialog: no passwords and no access to your phone." },
      {
        title: "Enter services and rules",
        text: "From a price list, a photo or text: prices, durations, staff, opening hours, cancellation rules. You check everything before the assistant starts.",
      },
      {
        title: "The assistant replies and books",
        text: "It answers only from the salon's data, offers free times and books them into the calendar. When it doesn't know, it hands the conversation to a staff member.",
      },
    ],
  },
  features: {
    title: "What you get",
    items: [
      { title: "Calendar", text: "Services with price and duration, staff, schedules, days off and manual booking in two taps." },
      {
        title: "AI replies",
        text: "Prices, services, address, opening hours and rules: only from the salon's data. If there is no answer, the assistant says it will check with the staff.",
      },
      { title: "Staff stay in charge", text: "Notifications to all staff, a “take over” button, the assistant stays silent while a person is replying." },
      { title: "Booking page", text: "A link for the Instagram profile and a QR code: clients book without chatting." },
      { title: "Monthly summary", text: "One line a month: how many clients the assistant booked and for how much." },
      { title: "No prepayments", text: "We never charge your clients and never send bulk messages." },
    ],
  },
  pricingTeaser: {
    title: "One price, no commissions",
    price: "€25",
    per: "per month",
    points: ["First month is a trial, first charge after 30 days", "No limit on conversations (with a daily anti-spam safeguard)", "Cancel any time"],
    cta: "Full pricing",
    setup: "Self setup: free. Done for you: €30 one-time.",
  },
  faq: {
    title: "Frequently asked questions",
    items: [
      {
        q: "Do clients know they are talking to an assistant?",
        a: "The assistant doesn't introduce itself, but answers honestly when asked directly. In settings you can make it say so right away.",
      },
      {
        q: "What if the assistant doesn't know the answer?",
        a: "It tells the client it will check with the staff and hands the conversation to you. Answer once, and the assistant remembers it next time.",
      },
      {
        q: "Can I take over a conversation?",
        a: "Yes, at any moment. While a staff member is replying, the assistant stays silent. It can also be paused completely.",
      },
      {
        q: "What do I need to connect?",
        a: "The salon's professional Instagram account and a Meta business portfolio. Connection goes through the Meta dialog; you never give us passwords.",
      },
      {
        q: "How long does setup take?",
        a: "About 15 minutes with the setup wizard: services, staff, opening hours, rules. If you prefer, we set it up for you for a one-time €30.",
      },
      {
        q: "Do you charge my clients anything?",
        a: "No. No prepayments, deposits or bulk messages. You pay only the salon subscription.",
      },
      {
        q: "Can I cancel?",
        a: "Yes, any time from the billing page. Access continues until the end of the paid period.",
      },
    ],
  },
  pricing: {
    title: "Pricing",
    subtitle: "One subscription for the whole salon: calendar, assistant, booking page and monthly summary.",
    plan: "Subscription",
    price: "€25",
    per: "per month",
    points: [
      "No commissions and no limits on conversations (with a daily anti-spam safeguard)",
      "First month is a trial: card required, first charge after 30 days",
      "Cancel any time, no annual contract",
    ],
    setupTitle: "Setup",
    self: { title: "Self setup", price: "Free", text: "A setup wizard, about 15 minutes." },
    done: {
      title: "Done for you",
      price: "€30 one-time",
      text: "We enter services, prices and rules and test the assistant. Paid together with the subscription.",
    },
    vat: "Any applicable tax is calculated by Paddle at checkout based on the buyer's country and shown before you pay.",
    mor: "Payments are processed by Paddle.com, the Merchant of Record for all our orders.",
    start: "Get started",
  },
  footer: {
    rights: "All rights reserved.",
    tagline: "A calendar and an AI assistant for salons that work by appointment.",
    contact: "Contact",
    language: "Language",
    legal: "Documents",
  },
  draft: "Draft: this text has not yet been reviewed by a lawyer.",
  updated: "Last updated",
};
